import {useContext, useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {STATE_SUMMARY_UPDATING} from "../../../constants/FinanceRecoil";
import {FilterDef} from "../../../constants/FinanceModels";
import {AddRounded, DescriptionRounded, DownloadRounded} from "@mui/icons-material";
import {Button, CircularProgress, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import {formatCurrency, formatDate} from "../../../utils/FormatUtils";
import {getLocalGridState, setLocalGridState} from "../../../utils/GridUtils";
import {
    STATE_PRINTOUT_ACTIVE_FILTERS,
    STATE_PRINTOUT_TOTAL_COUNT,
    STATE_REPORT_UPDATING
} from "../constants/RoyaltiesRecoil";
import ColouredChip from "../../../component/ColouredChip";
import {ID_ERROR, ID_PROCESSING, ID_PUBLISHED, ID_TO_SEND} from "../constants/RoyaltiesStatuses";
import {COLOR_ERROR, COLOR_PUBLISHED, COLOR_TO_SEND} from "../constants/RoyaltiesColors";
import {useRoyaltiesPrintoutRepository} from "../../../api/repositories/RoyaltiesPrintoutRepository";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {useNavigate} from "react-router-dom";
import {NumericSummary} from "../../../component/NumericSummary";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import {useAgGridLocale, useHasScope} from "../../../../../config/globalConfig";
import {CREATE_PRINTOUT, DOWNLOAD_PRINTOUT} from "../../../utils/FinanceScope";
import {downloadPDF} from "./ReportTableUtil";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import {decode} from "../../../utils/Base64Utils";

const PrintoutTable = () => {

    const UNIQUE_ID = "fnn-royalties-printout";

    const { t } = useTranslation('finance', { keyPrefix: 'royalties' });
    const { getPrintouts, getSummary, downloadPrintout } = useRoyaltiesPrintoutRepository();
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const [updating, setUpdating] = useRecoilState(STATE_REPORT_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const setTotalDataCount = useSetRecoilState(STATE_PRINTOUT_TOTAL_COUNT);
    const agGridLocale = useAgGridLocale();

    const filterDefs = useRecoilValue(STATE_PRINTOUT_ACTIVE_FILTERS);

    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);

    const {  globalData: { user } } = useContext(GlobalContext);
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");


    const columnDefs : ColDef[] = [
        {
            field: 'royaltyPrintoutRequestName', headerName: t('tables.printout.report-name'), headerTooltip: t('tables.printout.report-name'),
            suppressSizeToFit: true,
            minWidth: 150,
            unSortIcon: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true,
            sortable: true
        },
        {
            field: 'royaltyPrintoutRequestCreationDate', headerName: t('tables.printout.report-creation-date'), headerTooltip: t('tables.printout.report-creation-date'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value),
            sortable: true
        },
        {
            field: 'msName', headerName: t('tables.printout.dmsName'), headerTooltip: t('tables.printout.dmsName'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'ctName', headerName: t('tables.printout.name'), headerTooltip: t('tables.printout.name'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'ctType', headerName: t('tables.printout.contractor'), headerTooltip: t('tables.printout.contractor'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'totalCommissionable', headerName: t('tables.printout.total-commissionable'), headerTooltip: t('tables.printout.total-commissionable'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: 'sentDate', headerName: t('tables.printout.sent-date'), headerTooltip: t('tables.printout.sent-date'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: "legalEntity", headerName: t("tables.printout.legal-entity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.printout.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: 'status', headerName: t('tables.printout.status'), headerTooltip: t('tables.printout.status'),
            minWidth: 150, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_TO_SEND:
                        return {
                            component: () => <ColouredChip name={ID_TO_SEND} color={COLOR_TO_SEND}/>
                        };
                    case ID_PUBLISHED:
                        return {
                            component: () => <ColouredChip name={ID_PUBLISHED} color={COLOR_PUBLISHED}/>
                        };
                    case ID_PROCESSING:
                        return {
                            component: () => (
                                <Stack display={"flex"} justifyContent={"center"} alignItems={"center"} sx={{ height: '35px' }} direction={'row'} spacing={1}>
                                    <Typography fontSize={'small'}>
                                        {t(`chips.${ID_PROCESSING}`)}
                                    </Typography>
                                    <CircularProgress size={15}/>
                                </Stack>
                            )
                        };
                    case ID_ERROR:
                        return {
                            component: () =>
                                <Tooltip title={params.data.errorReason ? t('tables.printout.' + params.data.errorReason) : " "}>
                                    <div style={{ display: "inline-block" }}>
                                        <ColouredChip name={ID_ERROR} color={COLOR_ERROR}/>
                                    </div>
                                </Tooltip>
                        };
                    default:
                        break;
                }
            }
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                        <FinanceDynamicActions>
                            {hasScope(DOWNLOAD_PRINTOUT) &&
                                <>
                                    <IconButton sx={{color: theme.palette.accent.main}}
                                              disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR}
                                              onClick={() => {
                                                  downloadPrintout(params.data.id, "html").then((res) => {
                                                      if (res?.result) {
                                                          let pdfWindow = window.open("");
                                                          pdfWindow!.document.write(decode(res.result));
                                                      }
                                                  });
                                              }}>
                                        <DescriptionRounded/>
                                    </IconButton>
                                    <IconButton sx={{color: theme.palette.accent.main}}
                                                         disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR}
                                                         onClick={() => {
                                                             downloadPrintout(params.data.id, "pdf").then((res) => {
                                                                 if (res?.result) {
                                                                     downloadPDF(res.result, params.data);
                                                                 }
                                                             });
                                                         }}>
                                        <DownloadRounded/>
                                    </IconButton>
                                </>
                            }
                        </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.printout.no-rows.title'), content: t('tables.printout.no-rows.content') }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getPrintouts(startRow, endRow - startRow, sort, order, filterDefs)
                    .then(response => {
                        let result = response.result;
                        setTotalDataCount(result?.page.totalElements);
                        if (result.page.totalElements === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.page.totalElements
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
            <NumericSummary query={() => getSummary(filterDefs)} fields={[ID_PROCESSING, ID_TO_SEND, ID_PUBLISHED, ID_ERROR]} i18nPath={"royalties.counters.printouts"}/>
        ),
        renderActions: () => (
            <Stack>
                {hasScope(CREATE_PRINTOUT) &&
                <Button fullWidth color={"accent"} variant="contained" component="label" onClick={() => {
                    navigate("/finance/royalties/printouts-wizard")
                }} startIcon={<AddRounded/>}>{t('buttons.add-printout')}</Button>
                }
            </Stack>
        )
    }

    return (
        <AgGridCustomTable
            toolbar={toolbar}
            rowModelType='serverSide'
            paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
            gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
            rowSelection={'multiple'}
            noRowsOverlayComponent={noRowsOverlayComponent}
            suppressRowClickSelection
            onGridReady={() => {
                gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                let gridState = getLocalGridState(UNIQUE_ID).cols;
                if (gridState !== undefined) {
                    gridRef.current!.columnApi.applyColumnState({
                        state: gridState,
                        applyOrder: true
                    })
                }
            }}
            onColumnMoved={e => {
                if (e.source === "api") return;
                saveState();
            }}
            onColumnPinned={e => {
                if (e.source === "api") return;
                saveState();
            }}
            onColumnResized={e => {
                if (e.source === "api") return;
                saveState();
            }}
            onSortChanged={e => {
                if (e.source === "api") return;
                saveState();
            }}
            onPaginationChanged={() => {
                setLocalGridState(UNIQUE_ID, {
                    rows: gridRef.current!.api?.paginationGetPageSize(),
                    cols: getLocalGridState(UNIQUE_ID).cols
                });
            }}
        />
    );
}

export default PrintoutTable;